import React, { useMemo } from 'react'
import { formatDate } from '@/lib/utils'
import { computeNextRunDate, type Frequency } from '@/lib/recurringInvoiceCadence'

/**
 * Shared cadence controls for MakeRecurringModal (create) and
 * RecurringInvoiceDetail (edit), and (Piece 3b-ii-B) the upcoming
 * NewRecurringScheduleModal.
 *
 * Extracted verbatim from the byte-identical cadence sections in those
 * pages. Four cleanly-parameterizable knobs cover both existing usages:
 *
 *   - previewHeader: 'first' | 'next'
 *       Preview opener word — "First invoice {…}" (create) vs
 *       "Next run {…}" (edit).
 *   - showSweepNote: boolean
 *       The "Daily sweep runs at 00:15 UTC — the first invoice will
 *       land within ~24h." sub-note. Defaults to false; create passes
 *       true when start is today/past.
 *   - inlineNotice: ReactNode
 *       Optional banner rendered between preview and end-condition.
 *       The detail page uses this for the frequency-change disclosure
 *       ("the next run stays as scheduled; new cadence applies after").
 *       Modal passes null/omits.
 *
 * Title input is deliberately NOT included — each parent keeps its own
 * title input above this component because their behaviors differ:
 *   - MakeRecurringModal: title auto-defaults from contact name via a
 *     `titleTouched` flag that stops once the user edits.
 *   - RecurringInvoiceDetail: plain edit, hydrated from series.title.
 * Forcing title into the component would either pollute the API with a
 * create-specific quirk or lose 3a behavior.
 *
 * The component is fully controlled — parent owns the state, passes
 * value props + 1:1 setter callbacks. No internal useState for cadence
 * values; matches the existing setX semantics in both parents.
 *
 * The end-condition + send-mode radio JSX and the inp/lbl styles are
 * byte-identical copies of what was inlined in the parents — the
 * extracted version must render visually unchanged.
 */

export type EndCondition = 'never' | 'on_date' | 'after_count'
export type SendMode = 'draft' | 'auto_send'

const FREQUENCY_OPTIONS: { value: Frequency; label: string }[] = [
  { value: 'weekly',    label: 'Weekly' },
  { value: 'monthly',   label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly',    label: 'Yearly' },
]

interface Props {
  // Value props
  frequency: Frequency
  startDate: string
  endCondition: EndCondition
  endDate: string
  endAfterCount: string
  sendMode: SendMode

  // Change callbacks — single-field setters mirror the parents' useState setters
  onFrequencyChange:     (f: Frequency) => void
  onStartDateChange:     (d: string) => void
  onEndConditionChange:  (c: EndCondition) => void
  onEndDateChange:       (d: string) => void
  onEndAfterCountChange: (n: string) => void
  onSendModeChange:      (m: SendMode) => void

  // Display variants
  previewHeader?: 'first' | 'next'   // default 'next'
  showSweepNote?: boolean             // default false
  inlineNotice?: React.ReactNode      // optional banner rendered between preview and end-condition
}

export default function CadencePicker({
  frequency, startDate, endCondition, endDate, endAfterCount, sendMode,
  onFrequencyChange, onStartDateChange, onEndConditionChange,
  onEndDateChange, onEndAfterCountChange, onSendModeChange,
  previewHeader = 'next',
  showSweepNote = false,
  inlineNotice,
}: Props) {
  // Local styles — byte-identical copies of the parents' inp/lbl.
  const inp: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }
  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }

  // Preview — chain computeNextRunDate × 2 from startDate.
  const preview = useMemo(() => {
    if (!startDate) return []
    try {
      const dates = [startDate]
      for (let i = 0; i < 2; i++) dates.push(computeNextRunDate(dates[i], frequency))
      return dates
    } catch {
      return []
    }
  }, [startDate, frequency])

  // The preview opener: "First invoice" (create) vs "Next run" (edit).
  const previewOpener = previewHeader === 'first' ? 'First invoice' : 'Next run'

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={lbl}>Frequency</label>
          <select value={frequency} onChange={e => onFrequencyChange(e.target.value as Frequency)} style={{ ...inp, cursor: 'pointer' }}>
            {FREQUENCY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Start date</label>
          <input type="date" value={startDate} onChange={e => onStartDateChange(e.target.value)}
            style={{ ...inp, colorScheme: 'dark' }}
            onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'}
            onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
        </div>
      </div>

      {/* Cadence preview — uses computeNextRunDate from @/lib/recurringInvoiceCadence */}
      {preview.length === 3 && (
        <div style={{ padding: '12px 14px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10 }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Preview</p>
          <p style={{ fontSize: 12, color: 'var(--ink-secondary)', margin: 0, lineHeight: 1.6 }}>
            {previewOpener} <strong style={{ color: 'var(--ink-primary)' }}>{formatDate(preview[0], 'MMM d, yyyy')}</strong>, then{' '}
            <strong style={{ color: 'var(--ink-primary)' }}>{formatDate(preview[1], 'MMM d, yyyy')}</strong>, then{' '}
            <strong style={{ color: 'var(--ink-primary)' }}>{formatDate(preview[2], 'MMM d, yyyy')}</strong>, …
          </p>
          {showSweepNote && (
            <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '8px 0 0' }}>
              Daily sweep runs at 00:15 UTC — the first invoice will land within ~24h.
            </p>
          )}
        </div>
      )}

      {/* Parent-supplied inline notice — e.g. frequency-change disclosure on the detail page */}
      {inlineNotice}

      <div>
        <label style={lbl}>Ends</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {([
            { v: 'never',       label: 'Never (keep generating forever)' },
            { v: 'on_date',     label: 'On a specific date' },
            { v: 'after_count', label: 'After N invoices' },
          ] as { v: EndCondition; label: string }[]).map(opt => (
            <label key={opt.v} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: endCondition === opt.v ? 'var(--accent-dim)' : 'var(--field-bg)', border: `1px solid ${endCondition === opt.v ? 'var(--accent-border)' : 'var(--border-default)'}`, borderRadius: 8, cursor: 'pointer', fontSize: 13, color: 'var(--ink-primary)' }}>
              <input type="radio" name="end-condition" checked={endCondition === opt.v}
                onChange={() => onEndConditionChange(opt.v)}
                style={{ cursor: 'pointer' }} />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
        {endCondition === 'on_date' && (
          <input type="date" value={endDate} onChange={e => onEndDateChange(e.target.value)}
            style={{ ...inp, colorScheme: 'dark', marginTop: 8 }}
            onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'}
            onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
        )}
        {endCondition === 'after_count' && (
          <input type="number" min={1} value={endAfterCount} onChange={e => onEndAfterCountChange(e.target.value)}
            placeholder="e.g. 12"
            style={{ ...inp, marginTop: 8 }}
            onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'}
            onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
        )}
      </div>

      <div>
        <label style={lbl}>When generated</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 10px', background: sendMode === 'draft' ? 'var(--accent-dim)' : 'var(--field-bg)', border: `1px solid ${sendMode === 'draft' ? 'var(--accent-border)' : 'var(--border-default)'}`, borderRadius: 8, cursor: 'pointer', fontSize: 13, color: 'var(--ink-primary)' }}>
            <input type="radio" name="send-mode" checked={sendMode === 'draft'} onChange={() => onSendModeChange('draft')} style={{ marginTop: 2, cursor: 'pointer' }} />
            <div>
              <p style={{ margin: 0, fontWeight: 600 }}>Save as draft</p>
              <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--ink-tertiary)' }}>You review and send each invoice manually.</p>
            </div>
          </label>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 10px', background: sendMode === 'auto_send' ? 'var(--accent-dim)' : 'var(--field-bg)', border: `1px solid ${sendMode === 'auto_send' ? 'var(--accent-border)' : 'var(--border-default)'}`, borderRadius: 8, cursor: 'pointer', fontSize: 13, color: 'var(--ink-primary)' }}>
            <input type="radio" name="send-mode" checked={sendMode === 'auto_send'} onChange={() => onSendModeChange('auto_send')} style={{ marginTop: 2, cursor: 'pointer' }} />
            <div>
              <p style={{ margin: 0, fontWeight: 600 }}>Auto-send <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 100, background: 'var(--warning-dim)', color: 'var(--warning)', marginLeft: 6, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Live</span></p>
              <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--ink-tertiary)' }}>Generated invoices are emailed automatically and marked sent.</p>
            </div>
          </label>
        </div>
      </div>
    </>
  )
}
